import React, { useState, useEffect, useCallback, useMemo } from 'react';
import TraceabilityMain from './TraceabilityMain';
import TraceabilityFilters, { BRAND_OPTIONS } from './TraceabilityFilters';
import DistanceChartPanel from '../../charts/DistanceChartPanel';
import ConsumptionChartPanel from '../../charts/ConsumptionChartPanel';
import TraceabilityTable from './TraceabilityTable';
import SummaryPieChart from '../../cards/SummaryPieChart';
import TopSpenderSummary from '../../cards/TopSpenderSummary';
import { traceabilityConfigService } from '../../../services/traceabilityConfigService';
import { Activity, FileSpreadsheet, FileText } from 'lucide-react';
import { exportTraceabilityExcel, exportTraceabilityPDF } from '../../../utils/exporters/traceabilityExporter';

const MONTH_MAP = {
  'Enero': '01', 'Febrero': '02', 'Marzo': '03', 'Abril': '04',
  'Mayo': '05', 'Junio': '06', 'Julio': '07', 'Agosto': '08',
  'Septiembre': '09', 'Octubre': '10', 'Noviembre': '11', 'Diciembre': '12'
};

const getLastDayOfMonth = (year, monthIndex) => {
  return new Date(year, monthIndex, 0).getDate();
};

const TraceabilityPage = () => {
  // Estado
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // ESTADO DE DATOS GLOBAL (Estrategia de Paginación del Cliente)
  // Obtenemos todos los datos relevantes (hasta 5000 registros) una vez y derivamos todo de esto.
  // Esto asegura la consistencia entre la Tabla, Gráficos y el Mayor Consumidor.
  const [allSegments, setAllSegments] = useState([]);
  const [reloadTrigger, setReloadTrigger] = useState(0);

  // Estado de Paginación
  const [uiPage, setUiPage] = useState(1);
  const TABLE_PAGE_SIZE = 15;

  // Valores por defecto
  const currentDate = new Date();
  const currentYearStr = currentDate.getFullYear().toString();
  const monthNames = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
  const currentMonthName = monthNames[currentDate.getMonth()];
  const prevDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
  const prevMonthName = monthNames[prevDate.getMonth()];
  const prevYearStr = prevDate.getFullYear().toString();

  const [filters, setFilters] = useState({
    brands: [],
    startYear: prevYearStr,
    endYear: currentYearStr,
    startMonth: prevMonthName,
    endMonth: currentMonthName
  });

  // Auxiliar para construir parámetros (Usado principalmente para Lógica de Fechas)
  const getParamsForFetch = useCallback(() => {
    const sYear = filters.startYear;
    const eYear = filters.endYear;
    const startMonth = MONTH_MAP[filters.startMonth] || '01';
    const endMonthRaw = MONTH_MAP[filters.endMonth] || '12';
    const endMonthIndex = parseInt(endMonthRaw, 10);
    const lastDay = getLastDayOfMonth(eYear, endMonthIndex);

    return {
      fecha_inicio: `${sYear}-${startMonth}-01`,
      fecha_fin: `${eYear}-${endMonthRaw}-${lastDay}`,
      page_size: 5000,
      page: 1
    };
  }, [filters]);

  // Auxiliar: Obtener lista de { año, mesStr, etiqueta } entre fechas
  const getMonthRange = (sYear, sMonthName, eYear, eMonthName) => {
    const startMIdx = MONTH_MAP[sMonthName] ? parseInt(MONTH_MAP[sMonthName], 10) - 1 : 0;
    const endMIdx = MONTH_MAP[eMonthName] ? parseInt(MONTH_MAP[eMonthName], 10) - 1 : 11;

    const start = new Date(parseInt(sYear), startMIdx, 1);
    const end = new Date(parseInt(eYear), endMIdx, 1);

    const range = [];
    let current = new Date(start);

    while (current <= end) {
      const y = current.getFullYear();
      const m = current.getMonth() + 1; // 1-12
      const mStr = String(m).padStart(2, '0');
      const label = current.toLocaleString('es-MX', { month: 'short', year: 'numeric' });
      range.push({ year: y.toString(), monthStr: mStr, label });
      current.setMonth(current.getMonth() + 1);
    }
    return range;
  };

  // --- MAIN FETCH EFFECT ---
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      setUiPage(1); // Reset page on filter change

      try {
        const baseParams = getParamsForFetch();

        // Determinar marcas
        const targetBrands = (filters.brands && filters.brands.length > 0)
          ? filters.brands
          : BRAND_OPTIONS;

        // Búsqueda en paralelo para todas las marcas
        const promises = targetBrands.map(brand => {
          const p = { ...baseParams, marca: brand };
          return traceabilityConfigService.getHistoricalTraceability(p)
            .then(res => getSegments(res))
            .catch(err => {
              console.warn(`Falló la búsqueda para ${brand}`, err);
              return [];
            });
        });

        const results = await Promise.all(promises);
        // Combinar y Ordenar Globalmente
        const combined = results.flat().sort((a, b) => (b.id || 0) - (a.id || 0));

        setAllSegments(combined);

      } catch (err) {
        console.error("Error loading traceback data", err);
        setError("Error al cargar la información.");
        setAllSegments([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [filters, getParamsForFetch, reloadTrigger]);

  // --- LISTEN FOR RELOAD EVENTS ---
  useEffect(() => {
    const handleImmediateUpdate = () => {
      setReloadTrigger(prev => prev + 1);
    };

    window.addEventListener('rfidLecturaSimulada', handleImmediateUpdate);
    window.addEventListener('vehiculosSalidaActualizada', handleImmediateUpdate);
    window.addEventListener('unidadesNoMayoreadasActualizadas', handleImmediateUpdate);
    window.addEventListener('refreshTraceabilityData', handleImmediateUpdate);

    return () => {
      window.removeEventListener('rfidLecturaSimulada', handleImmediateUpdate);
      window.removeEventListener('vehiculosSalidaActualizada', handleImmediateUpdate);
      window.removeEventListener('unidadesNoMayoreadasActualizadas', handleImmediateUpdate);
      window.removeEventListener('refreshTraceabilityData', handleImmediateUpdate);
    };
  }, []);

  // --- DATOS DERIVADOS ---

  // 1. Datos de Tabla Paginados
  const paginatedSegments = useMemo(() => {
    if (!allSegments || allSegments.length === 0) return [];
    const start = (uiPage - 1) * TABLE_PAGE_SIZE;
    const end = start + TABLE_PAGE_SIZE;
    return allSegments.slice(start, end);
  }, [allSegments, uiPage]);

  // 2. Mayor Consumidor (Global Exacto porque tenemos todos los segmentos)
  const globalTopSpender = useMemo(() => {
    if (!allSegments || allSegments.length === 0) return null;

    const vehicleStats = {};
    allSegments.forEach(seg => {
      const vin = seg.vin || seg.vehiculo?.vin;
      if (!vin) return;
      if (!vehicleStats[vin]) {
        vehicleStats[vin] = {
          litros: 0,
          km: 0,
          vehiculo: seg.vehiculo // Keep reference
        };
      }
      vehicleStats[vin].litros += (seg.litros_consumidos || 0);
      vehicleStats[vin].km += (seg.distancia_km || 0);
    });

    let topVin = null;
    let maxLitros = -1;
    Object.keys(vehicleStats).forEach(vin => {
      if (vehicleStats[vin].litros > maxLitros) {
        maxLitros = vehicleStats[vin].litros;
        topVin = vin;
      }
    });

    if (topVin) {
      const winner = vehicleStats[topVin];
      const finalVehiculo = winner.vehiculo ? { ...winner.vehiculo, vin: topVin } : { vin: topVin };
      return {
        vehiculo: finalVehiculo,
        totalDistance: winner.km,
        totalConsumption: winner.litros
      };
    }
    return null;
  }, [allSegments]);

  // 3. Datos de Gráficos (Derivados de Segmentos)
  const derivedChartData = useMemo(() => {
    if (!allSegments) return null;

    // Calcular Meses necesarios
    const months = getMonthRange(filters.startYear, filters.startMonth, filters.endYear, filters.endMonth);
    const targetBrands = (filters.brands && filters.brands.length > 0) ? filters.brands : BRAND_OPTIONS;

    const statsByBrand = {};
    targetBrands.forEach(b => {
      statsByBrand[b] = {};
      months.forEach(m => {
        statsByBrand[b][`${m.year}-${m.monthStr}`] = { distance: 0, consumption: 0 };
      });
    });

    // Iterar TODOS los segmentos para agregar
    allSegments.forEach(seg => {
      const brand = seg.vehiculo?.marca || 'OTRO';
      // Mapeo de fechas: Asumiendo seg.fecha_inicio o seg.origen.fecha es cadena ISO o similar
      // Trazabilidad usualmente tiene origen.fecha
      const dateStr = seg.origen?.fecha || seg.fecha_inicio;
      if (!dateStr) return;

      const date = new Date(dateStr);
      const y = date.getFullYear().toString();
      const m = (date.getMonth() + 1).toString().padStart(2, '0');
      const key = `${y}-${m}`;

      if (statsByBrand[brand] && statsByBrand[brand][key]) {
        statsByBrand[brand][key].distance += (seg.distancia_km || 0);
        statsByBrand[brand][key].consumption += (seg.litros_consumidos || 0);
      }
    });

    // Construir Datasets
    const getBrandColor = (brand, opacity = 1) => {
      const colors = {
        'BYD': `rgba(29, 78, 216, ${opacity})`,
        'BMW': `rgba(0, 68, 136, ${opacity})`,
        'JEEP': `rgba(34, 139, 34, ${opacity})`,
        'KIA': `rgba(192, 0, 0, ${opacity})`,
        'OTRO': `rgba(107, 114, 128, ${opacity})`
      };
      return colors[brand?.toUpperCase()] || colors['OTRO'];
    };

    const chartLabels = ['', ...months.map(m => m.label)];

    const distanceDatasets = targetBrands.map(brand => ({
      label: brand,
      data: [0, ...months.map(m => {
        const key = `${m.year}-${m.monthStr}`;
        return Number((statsByBrand[brand][key]?.distance || 0).toFixed(2));
      })],
      backgroundColor: getBrandColor(brand, 0.8),
      borderRadius: 4,
      barPercentage: 0.6,
    }));

    const consumptionDatasets = targetBrands.map(brand => ({
      label: brand,
      data: [0, ...months.map(m => {
        const key = `${m.year}-${m.monthStr}`;
        return Number((statsByBrand[brand][key]?.consumption || 0).toFixed(2));
      })],
      borderColor: getBrandColor(brand, 1),
      backgroundColor: getBrandColor(brand, 0.1),
      tension: 0.3,
      fill: false,
      pointBackgroundColor: '#fff',
      pointBorderColor: getBrandColor(brand, 1),
      pointHoverBackgroundColor: getBrandColor(brand, 1),
      pointHoverBorderColor: '#fff',
    }));

    const pieValues = targetBrands.map(brand => {
      let total = 0;
      for (const m of months) {
        total += statsByBrand[brand][`${m.year}-${m.monthStr}`]?.consumption || 0;
      }
      return Number(total.toFixed(2));
    });

    const pieChartData = {
      labels: targetBrands,
      values: pieValues,
      colors: targetBrands.map(b => getBrandColor(b, 0.8))
    };

    return { labels: chartLabels, distanceDatasets, consumptionDatasets, pieChartData };

  }, [allSegments, filters]);


  const handleFilterChange = (newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  const handlePageChange = (newPage) => {
    setUiPage(newPage);
  };

  // Auxiliar para extraer segmentos de CUALQUIER estructura de respuesta
  const getSegments = (dataObj) => {
    if (!dataObj) return [];
    if (dataObj.results && dataObj.results.segmentos) return dataObj.results.segmentos;
    if (dataObj.segmentos) return dataObj.segmentos;
    if (Array.isArray(dataObj.results)) return dataObj.results;
    if (Array.isArray(dataObj)) return dataObj;
    return [];
  };

  // Exportaciones
  const distanceChartRef = React.useRef(null);
  const consumptionChartRef = React.useRef(null);

  const handleExportExcel = () => {
    if (!allSegments || allSegments.length === 0) return;
    exportTraceabilityExcel(allSegments, filters);
  };

  const handleExportPdf = () => {
    if (!allSegments || allSegments.length === 0) return;

    const chartImages = {
      distanceChart: distanceChartRef.current?.toBase64Image(),
      consumptionChart: consumptionChartRef.current?.toBase64Image()
    };
    exportTraceabilityPDF(allSegments, filters, chartImages);
  };

  const EmptyState = () => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 h-full flex flex-col items-center justify-center text-center">
      <div className="bg-gray-50 p-3 rounded-full mb-3">
        <Activity className="w-6 h-6 text-gray-400" />
      </div>
      <h3 className="text-sm font-medium text-gray-900 mb-1">Sin datos</h3>
      <p className="text-xs text-gray-500">No hay registros para este periodo.</p>
    </div>
  );

  // Desestructurar para gráficos
  const { labels, distanceDatasets, consumptionDatasets, pieChartData } = derivedChartData || {};

  return (
    <TraceabilityMain>
      <div className="min-h-screen pb-10 flex flex-col gap-6">
        <div className="flex justify-end gap-3 px-1">
          <button onClick={handleExportExcel} disabled={!allSegments.length} className="flex items-center gap-2 px-4 py-2 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium border border-green-200" title="Exportar Todo a Excel">
            <FileSpreadsheet className="w-4 h-4" />
          </button>
          <button onClick={handleExportPdf} disabled={!allSegments.length} className="flex items-center gap-2 px-4 py-2 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium border border-red-200" title="Exportar Todo a PDF">
            <FileText className="w-4 h-4" />
          </button>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 shrink-0">
          <div className="w-full h-full">
            <TraceabilityFilters onChangeFilters={handleFilterChange} loading={loading} />
          </div>
          <div className="w-full h-full min-h-[250px]">
            {loading ? <div className="flex justify-center items-center h-full"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div> : pieChartData ? <SummaryPieChart data={pieChartData} period={`${filters.startMonth} ${filters.startYear} - ${filters.endMonth} ${filters.endYear}`} /> : <EmptyState />}
          </div>
          <div className="w-full h-full">
            {loading ? <div className="flex justify-center items-center h-full"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div> : <TopSpenderSummary data={globalTopSpender} />}
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
          {/* Charts using Global Data */}
          <div className="flex flex-col md:flex-row xl:flex-col gap-6 xl:col-span-2 xl:h-full">
            <div className="flex-1 min-w-0 h-[300px] xl:h-auto xl:min-h-[300px]">
              {loading ? <div className="h-full bg-white rounded-lg border border-gray-100 flex items-center justify-center"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div></div> : <DistanceChartPanel key={`distance-${filters.brands.join('-')}`} ref={distanceChartRef} data={{ labels, datasets: distanceDatasets }} />}
            </div>
            <div className="flex-1 min-w-0 h-[300px] xl:h-auto xl:min-h-[300px]">
              {loading ? <div className="h-full bg-white rounded-lg border border-gray-100 flex items-center justify-center"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div></div> : <ConsumptionChartPanel key={`consumption-${filters.brands.join('-')}`} ref={consumptionChartRef} data={{ labels, datasets: consumptionDatasets }} />}
            </div>
          </div>

          <div className="xl:col-span-3">
            {/* Table using Paginated Data */}
            <TraceabilityTable
              segments={paginatedSegments}
              loading={loading}
              error={error}
              totalItems={allSegments.length}
              initialPage={uiPage}
              onPageChange={handlePageChange}
              pageSize={TABLE_PAGE_SIZE}
            />
          </div>
        </div>
      </div>
    </TraceabilityMain>
  );
};

export default TraceabilityPage;